import React, { useState, useEffect, memo } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Play, MessageCircle, BookOpen, Cpu, Trophy, Users } from "lucide-react";

// --- Typing Effect Component ---
const TypingEffect = ({ strings, typeSpeed = 50, deleteSpeed = 30, pauseTime = 1500 }) => {
  const [displayedText, setDisplayedText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    let timer;
    const currentString = strings[currentIndex % strings.length];

    if (isDeleting) {
      if (displayedText.length > 0) {
        timer = setTimeout(() => {
          setDisplayedText(prev => prev.substring(0, prev.length - 1));
        }, deleteSpeed);
      } else {
        setIsDeleting(false);
        setCurrentIndex(prev => prev + 1);
      }
    } else {
      if (displayedText.length < currentString.length) {
        timer = setTimeout(() => {
          setDisplayedText(prev => prev + currentString.charAt(prev.length));
        }, typeSpeed);
      } else {
        timer = setTimeout(() => setIsDeleting(true), pauseTime);
      }
    }
    return () => clearTimeout(timer);
  }, [displayedText, isDeleting, currentIndex, strings, typeSpeed, deleteSpeed, pauseTime]);

  return (
    <span className="relative">
      {displayedText}
      <span className="ml-1 inline-block w-0.5 h-5 bg-primary animate-pulse align-middle" />
    </span>
  );
};

// --- Data ---
const curriculum = [
  "Engineering Mathematics: M1, M2, M3",
  "Programming: Python, Java for Schools",
  "Math for All Boards: 6th to 12th",
  "Computer Science: Python, Java for ICSE",
  "ICSE, CBSE, State Board: 11th & 12th",
  "Engineering: Python & Programming",
  "Python for Beginners or Non-Tech Professionals",
  "Machine Learning Basics for School Students",
];

const subjectCards = [
  { title: "Mathematics", students: "500+ Students", icon: BookOpen, color: "from-blue-500 to-cyan-500" },
  { title: "Programming", students: "300+ Students", icon: Cpu, color: "from-violet-500 to-purple-500" },
  { title: "Competitive Exams", students: "200+ Students", icon: Trophy, color: "from-orange-500 to-amber-500" },
  { title: "1:1 Mentorship", students: "Personalized", icon: Users, color: "from-emerald-500 to-teal-500" },
];

const scrollToSection = (id) => {
  const element = document.getElementById(id);
  if (element) element.scrollIntoView({ behavior: 'smooth' });
};

// --- Components ---
const SubjectCard = ({ title, students, icon: Icon, color }) => (
  <motion.div
    className="group relative bg-white p-4 rounded-2xl border border-slate-100 shadow-sm hover:shadow-xl transition-all duration-300 cursor-pointer overflow-hidden"
    whileHover={{ y: -4, scale: 1.02 }}
    transition={{ type: "spring", stiffness: 400, damping: 25 }}
  >
    <div className={`absolute top-0 left-0 w-full h-1 bg-gradient-to-r ${color}`} />
    <div className="flex items-center gap-3">
      <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${color} flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300`}>
        <Icon size={20} className="text-white" />
      </div>
      <div>
        <h3 className="font-semibold text-slate-800 text-sm">{title}</h3>
        <p className="text-xs text-slate-500">{students}</p>
      </div>
    </div>
  </motion.div>
);

const FloatingElement = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay, duration: 0.6, ease: "easeOut" }}
    className={className}
  >
    {children}
  </motion.div>
);

const Hero = () => {
  return (
    <section className="relative z-0 min-h-screen overflow-hidden bg-slate-50">
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-gradient-to-br from-blue-100/40 via-violet-100/30 to-purple-100/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/4" />
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-gradient-to-tr from-cyan-100/30 via-blue-100/20 to-violet-100/30 rounded-full blur-3xl translate-y-1/2 -translate-x-1/4" />
        <div className="absolute top-1/2 left-1/2 w-[400px] h-[400px] bg-gradient-to-r from-amber-100/20 to-orange-100/20 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
      </div>

      {/* Grid Pattern Overlay */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(99,102,241,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(99,102,241,0.03)_1px,transparent_1px)] bg-[size:60px_60px] pointer-events-none" />

      <div className="relative w-full mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 2xl:px-16 max-w-7xl 2xl:max-w-8xl 3xl:max-w-9xl pt-28 pb-16 lg:pt-32 lg:pb-20 2xl:pt-36 2xl:pb-24">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 2xl:gap-24 items-start">
          
          {/* Left Content */}
          <div className="text-center lg:text-left space-y-8">

            {/* Main Heading */}
            <FloatingElement delay={0.2}>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl 2xl:text-7xl 3xl:text-8xl font-bold text-slate-900 leading-[1.1] tracking-tight">
                Master{" "}
                <span className="relative inline-block">
                  <span className="bg-gradient-to-r from-blue-600 via-violet-600 to-purple-600 bg-clip-text text-transparent">
                    Math & Coding
                  </span>
                  <svg className="absolute -bottom-2 left-0 w-full" viewBox="0 0 300 12" fill="none">
                    <path d="M2 10C50 4 150 2 298 8" stroke="url(#gradient)" strokeWidth="3" strokeLinecap="round"/>
                    <defs>
                      <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#3B82F6"/>
                        <stop offset="50%" stopColor="#8B5CF6"/>
                        <stop offset="100%" stopColor="#A855F7"/>
                      </linearGradient>
                    </defs>
                  </svg>
                </span>
                <br />
                with Expert Tutors
              </h1>
            </FloatingElement>

            {/* Subheading */}
            <FloatingElement delay={0.3}>
              <p className="text-lg sm:text-xl 2xl:text-2xl text-slate-600 max-w-xl 2xl:max-w-2xl mx-auto lg:mx-0 leading-relaxed">
                From school to college, we make learning simple and stress-free. Expert tutors for Math, Coding & Exams.
              </p>
            </FloatingElement>

            {/* CTAs */}
            <FloatingElement delay={0.4}>
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <motion.button
                  onClick={() => scrollToSection('contact-us')}
                  className="group relative inline-flex items-center justify-center gap-2 px-8 py-4 bg-slate-900 text-white font-semibold rounded-2xl overflow-hidden shadow-xl shadow-slate-900/20 hover:shadow-2xl hover:shadow-slate-900/30 transition-all duration-300"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <span className="absolute inset-0 bg-gradient-to-r from-blue-600 to-violet-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <span className="relative flex items-center gap-2">
                    Start Free Trial
                    <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                  </span>
                </motion.button>
                
                <motion.button
                  onClick={() => scrollToSection('services')}
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-slate-700 font-semibold rounded-2xl border border-slate-200 shadow-sm hover:shadow-md hover:border-slate-300 transition-all duration-300"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Play size={18} className="text-blue-600" />
                  Explore Courses
                </motion.button>
              </div>
            </FloatingElement>

            {/* Trust Badge - Seal Design */}
            <FloatingElement delay={0.5}>
              <motion.div 
                className="flex items-center gap-3 pt-4"
                whileHover={{ scale: 1.02 }}
              >
                {/* Seal Badge */}
                <div className="relative">
                  {/* Outer ring */}
                  <div className="w-14 h-14 rounded-full bg-gradient-to-br from-amber-400 via-orange-500 to-rose-500 p-0.5">
                    {/* Inner white circle */}
                    <div className="w-full h-full rounded-full bg-white flex items-center justify-center">
                      {/* Checkmark */}
                      <motion.svg 
                        width="24" 
                        height="24" 
                        viewBox="0 0 24 24" 
                        fill="none"
                        initial={{ pathLength: 0 }}
                        animate={{ pathLength: 1 }}
                        transition={{ duration: 0.8, delay: 0.8 }}
                      >
                        <motion.path
                          d="M5 12L10 17L20 7"
                          stroke="url(#gradient)"
                          strokeWidth="3"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          fill="none"
                        />
                        <defs>
                          <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                            <stop offset="0%" stopColor="#F59E0B"/>
                            <stop offset="100%" stopColor="#EF4444"/>
                          </linearGradient>
                        </defs>
                      </motion.svg>
                    </div>
                  </div>
                  {/* Sparkle decorations */}
                  <motion.span
                    className="absolute -top-1 -right-1 text-lg"
                    animate={{ rotate: [0, 15, -15, 0], scale: [1, 1.2, 1] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  >
                    ✨
                  </motion.span>
                </div>
                
                {/* Trust Text */}
                <div className="flex flex-col text-left">
                  <span className="text-sm font-bold text-slate-800">Parent Verified</span>
                  <span className="text-xs text-slate-500">Trusted by 1,000+ families across 5+ countries</span>
                  <div className="flex items-center gap-1 mt-1">
                    <span className="text-amber-500 text-sm">⭐⭐⭐⭐✫</span>
                    <span className="text-xs text-slate-600 font-medium">4.5/5</span>
                    <span className="text-xs text-slate-400">rating</span>
                  </div>
                </div>
                
                {/* Mini avatars */}
                <div className="hidden sm:flex -space-x-1.5 ml-2">
                  {["from-blue-400", "from-violet-400", "from-amber-400"].map((color, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, scale: 0 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 1 + i * 0.1 }}
                      className={`w-6 h-6 rounded-full bg-gradient-to-br ${color} to-transparent border-2 border-white`}
                    />
                  ))}
                </div>
              </motion.div>
            </FloatingElement>

          </div>

          {/* Right Content - Cards Grid */}
          <div className="relative">
            <FloatingElement delay={0.3}>
              <div className="space-y-4">

                {/* Curriculum Card - Clean & Light */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8 }}
                  whileHover={{ scale: 1.02 }}
                  className="relative group"
                >
                  {/* Soft border glow */}
                  <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-400 to-violet-400 rounded-2xl opacity-30 group-hover:opacity-50 blur-sm transition-opacity duration-500" />
                  
                  {/* Card */}
                  <div className="relative bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
                    {/* Countries Badge - Elegant Design */}
                    <motion.div
                      className="absolute -top-1 -right-1 z-10"
                      whileHover={{ scale: 1.05 }}
                      transition={{ type: "spring", stiffness: 400, damping: 25 }}
                    >
                      <div className="relative">
                        {/* Diamond shape background */}
                        <div className="absolute inset-0 bg-gradient-to-br from-purple-500 to-pink-500 transform rotate-45 w-12 h-12"></div>
                        {/* Badge content */}
                        <div className="relative bg-white rounded-lg px-2 py-1.5 shadow-lg border border-purple-200">
                          <div className="flex flex-col items-center">
                            <div className="flex items-center gap-1">
                              <span className="text-xs font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">5+</span>
                              <span className="text-xs">🌍</span>
                            </div>
                            <span className="text-xs text-purple-600 font-medium">Countries</span>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                    
                    {/* Header with dot */}
                    <div className="flex items-center gap-3 mb-4">
                      <motion.div
                        className="relative w-2 h-2"
                        animate={{ scale: [1, 1.3, 1] }}
                        transition={{ duration: 2, repeat: Infinity }}
                      >
                        <div className="absolute inset-0 bg-blue-500 rounded-full blur-sm" />
                        <div className="relative w-2 h-2 bg-blue-500 rounded-full" />
                      </motion.div>
                      <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">Live Curriculum</span>
                    </div>
                    
                    {/* Typing text */}
                    <div className="relative">
                      <div className="text-lg font-semibold text-slate-800 min-h-[32px]">
                        <TypingEffect strings={curriculum} typeSpeed={40} deleteSpeed={20} pauseTime={2000} />
                      </div>
                      
                      {/* Underline */}
                      <motion.div
                        className="absolute -bottom-1 left-0 h-0.5 bg-gradient-to-r from-blue-400 to-violet-400"
                        animate={{ width: ["0%", "100%", "0%"] }}
                        transition={{ duration: 3, repeat: Infinity, delay: 0.5 }}
                      />
                    </div>
                    
                    {/* Bottom status */}
                    <div className="flex items-center gap-2 mt-4 pt-3 border-t border-slate-100">
                      <motion.div
                        className="w-1.5 h-1.5 rounded-full bg-green-500"
                        animate={{ opacity: [0.4, 1, 0.4] }}
                        transition={{ duration: 2, repeat: Infinity }}
                      />
                      <span className="text-xs text-slate-400">Active Now</span>
                    </div>
                  </div>
                </motion.div>

                {/* Subject Cards - Moved to bottom */}
                <div className="grid grid-cols-2 gap-3">
                  {subjectCards.map((card, index) => (
                    <motion.div
                      key={card.title}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.9 + index * 0.1 }}
                    >
                      <SubjectCard {...card} />
                    </motion.div>
                  ))}
                </div>
              </div>
            </FloatingElement>

            {/* Decorative Elements */}
            <motion.div
              className="absolute -top-4 -right-4 w-20 h-20 bg-gradient-to-br from-amber-400 to-orange-400 rounded-2xl opacity-20 blur-xl"
              animate={{ scale: [1, 1.2, 1], rotate: [0, 10, 0] }}
              transition={{ duration: 4, repeat: Infinity }}
            />
            <motion.div
              className="absolute -bottom-4 -left-4 w-16 h-16 bg-gradient-to-br from-blue-400 to-violet-400 rounded-full opacity-20 blur-xl"
              animate={{ scale: [1, 1.3, 1] }}
              transition={{ duration: 3, repeat: Infinity }}
            />
          </div>
        </div>
      </div>

      {/* Floating Chat Button */}
      <motion.a
        href="https://gmtn-chat-ai.streamlit.app/"
        target="_blank"
        rel="noopener noreferrer"
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 1, type: "spring", stiffness: 200 }}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        className="fixed bottom-6 right-6 z-50 flex items-center gap-3 px-5 py-4 bg-slate-900 text-white rounded-2xl shadow-2xl shadow-slate-900/30 hover:shadow-slate-900/50 transition-all duration-300 group"
      >
        <div className="relative">
          <MessageCircle size={24} />
          <span className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-slate-900" />
        </div>
        <div className="hidden sm:block">
          <p className="font-semibold text-sm">Chat with Isha</p>
          <p className="text-xs text-slate-400">AI Assistant</p>
        </div>
      </motion.a>
    </section>
  );
};

export default memo(Hero);
